import { useEffect, useState } from "react";
import { Link as RouterLink, Navigate, RouteObject, useParams } from "react-router-dom";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Skeleton, Button, CircularProgress, Stack, IconButton, Tooltip, Box } from "@mui/material";
import useDomains from "cohorts/hooks/useDomains";
import CollaboratorsModal from "datasets/components/CollaboratorsModal";
import { DatasetAssistantPanel } from "datasets/components/assistant/DatasetAssistantPanel";
import {
  loadDatasetAssistantState,
  updateDatasetAssistantState,
} from "datasets/components/assistant/datasetAssistantState";
import useDatasetAssistant from "datasets/components/assistant/hooks/useDatasetAssistant";
import useDataset from "datasets/hooks/useDataset";
import useIsMasterDataset from "datasets/hooks/useIsMasterDataset";
import DatasetReport from "datasets/pages/dashboards/pages/DatasetReport";
import DatasetReports from "datasets/pages/dashboards/pages/DatasetReports";
import DatasetFiles from "datasets/pages/files/DatasetFiles";
import DatasetOverview from "datasets/pages/overview/DatasetOverview";
import DatasetSettings from "datasets/pages/settings/DatasetSettings";
import DatasetTables from "datasets/pages/tables/DatasetTables";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen } from "lucide-react";
import yn from "yn";
import { CohortIcon } from "~/assets/icons";
import { HEADER_HEIGHT, TitleBreadcrumbs } from "~/components/AppHeader";
import RouteTabs, { ROUTE_TABS_HEIGHT, RouteTab } from "~/components/RouteTabs";
import useAppHeader from "~/contexts/header";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useSafeRoutes } from "~/hooks/useSafeRoutes";
import { ROUTE_404 } from "~/router/constants";
import { trackEvent } from "~/utils/monitoring";

const { VITE_COHORT_EXPLORER_ENABLED, VITE_DATASET_FILES_ENABLED } = import.meta.env;

const routeDefinitions: RouteObject[] = [
  { index: true, element: <Navigate to="overview" replace /> },
  { path: "overview", element: <DatasetOverview /> },
  {
    path: "reports",
    element: <DatasetReports />,
  },
  {
    path: "analysis",
    element: <Navigate to="../overview" replace />,
  },
  {
    path: "analysis-v2",
    element: <Navigate to="../overview" replace />,
  },
  {
    path: "reports/:reportId",
    element: <DatasetReport />,
  },
  {
    path: "tables/*",
    element: <DatasetTables />,
  },
  {
    path: "files/:prefixId?/*",
    element: <DatasetFiles />,
  },

  {
    path: "settings/*",
    element: <DatasetSettings />,
  },
];

const DatasetDetails = () => {
  const { datasetId, "*": subRoute } = useParams<{ datasetId: string; "*": string }>();
  const routes = useSafeRoutes(routeDefinitions);
  const { data: dataset, isLoading, error } = useDataset(datasetId);
  const isMasterDataset = useIsMasterDataset();
  const showSettings = dataset?.access_level === DatasetAccessLevel.OWNER;
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [initialAssistantState] = useState(() => loadDatasetAssistantState());
  const [isAssistantCollapsed, setIsAssistantCollapsed] = useState(
    () => initialAssistantState.collapsed
  );
  const [assistantPosition, setAssistantPosition] = useState(() => initialAssistantState.position);
  const toggleAssistantCollapsed = () => {
    setIsAssistantCollapsed((prev) => {
      const nextCollapsed = !prev;
      updateDatasetAssistantState({ collapsed: nextCollapsed });
      return nextCollapsed;
    });
  };
  const isPreviewUser = dataset?.access_level === DatasetAccessLevel.PREVIEW;

  const aiAgentsEnabled = dataset?.aiAgentsEnabled === true;
  const showAnalysis = useDatasetAssistant() && !isPreviewUser && aiAgentsEnabled;
  const showAssistant = showAnalysis;
  const { data: domains } = useDomains();
  const cohortExplorerEnabled = yn(VITE_COHORT_EXPLORER_ENABLED) && !!domains?.length;

  const currentTab = subRoute?.split("/")[0] || "overview";
  const isReportEmbed = currentTab === "reports" && subRoute?.split("/")[1];

  useDocumentTitle(dataset?.title);
  const { setTitle } = useAppHeader();

  // Track dataset views only if dataset or tab has changed
  useEffect(() => {
    if (dataset?.id && currentTab) {
      trackEvent("dataset_viewed", {
        dataset_id: dataset.id,
        page: currentTab === "reports" ? "dashboards" : currentTab,
      });
    }
  }, [dataset?.id, currentTab]);

  useEffect(() => {
    setTitle(
      <TitleBreadcrumbs
        crumbs={[
          { title: "Data Browser", href: "/data-browser" },
          { title: "Datasets", href: "/data-browser/datasets" },
          { title: isLoading ? <Skeleton width={75} /> : dataset?.title, href: location.pathname },
        ]}
      />
    );
  }, [dataset, isLoading, setTitle]);

  // Sanitize tab value to prevent MUI TabContext errors
  const getValidTabValue = () => {
    if (currentTab === "settings") {
      return null;
    }
    if (isPreviewUser) {
      const allowedTabs = ["overview", "reports"];
      if (!allowedTabs.includes(currentTab)) {
        return "overview";
      }
    }
    return currentTab;
  };

  if (error?.status === 404) {
    return <Navigate to={ROUTE_404} replace />;
  }

  if (!showSettings && currentTab === "settings" && !isLoading) {
    return <Navigate to="/404" replace />;
  }

  // Redirect preview users trying to access restricted routes
  if (isPreviewUser && !isLoading) {
    const restrictedRoutes = ["analysis", "analysis-v2", "tables", "files"];
    if (restrictedRoutes.includes(currentTab)) {
      return <Navigate to="overview" replace />;
    }
  }

  // Build tabs based on permissions and feature flags
  const tabs: RouteTab[] = [
    { label: "Overview", value: "overview" },
    { label: "Dashboards", value: "reports" },
    {
      label: "Tables",
      value: "tables",
      visible: !isPreviewUser,
    },
    {
      label: "Files",
      value: "files",
      visible: yn(VITE_DATASET_FILES_ENABLED) && !isMasterDataset && !isPreviewUser,
    },
  ];

  // Action buttons for the tab bar
  const renderActions = () => {
    const accessLevel = dataset?.access_level;
    const showShare = accessLevel === DatasetAccessLevel.OWNER;
    const showCohort = cohortExplorerEnabled && !!dataset;
    if (!showSettings && !showShare && !showAssistant && !showCohort) {
      return null;
    }

    return (
      <Stack direction="row" spacing={0.75} alignItems="center">
        {showCohort && (
          <Button
            component={RouterLink}
            to="/cohorts/new"
            state={{ datasetId: dataset.id }}
            size="small"
            startIcon={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  color: "common.white",
                  "& > *": { width: 15, height: 15 },
                }}
              >
                <CohortIcon />
              </Box>
            }
            sx={{
              backgroundColor: "grey.200",
              color: "grey.800",
              textTransform: "none",
              fontWeight: 500,
              fontSize: 13.5,
              height: 32,
              px: 1.5,
              "&:hover": { backgroundColor: "grey.300", color: "grey.900" },
            }}
          >
            Build a Cohort
          </Button>
        )}
        {showShare && (
          <Button
            data-testid="share-dataset-button"
            variant="contained"
            onClick={() => setIsShareModalOpen(true)}
            sx={{
              backgroundColor: "grey.200",
              color: "grey.800",
              textTransform: "none",
              fontWeight: 500,
              fontSize: 14,
              height: 32,
              px: 1.5,
              "&:hover": { backgroundColor: "grey.300", color: "grey.900" },
            }}
          >
            Share
          </Button>
        )}
        {showSettings && (
          <Tooltip arrow enterDelay={300} title="Dataset Settings">
            <IconButton
              data-testid="dataset-settings-button"
              component={currentTab === "settings" ? "button" : RouterLink}
              to={
                currentTab === "settings"
                  ? undefined
                  : `/data-browser/datasets/${datasetId}/settings`
              }
              size="small"
              sx={{
                color: "grey.600",
                height: 32,
                width: 32,
                "&:hover": { backgroundColor: "grey.100", color: "grey.800" },
              }}
            >
              <SettingsOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {showAssistant && (
          <Tooltip arrow enterDelay={300} title="Toggle Analysis Agent">
            <IconButton
              data-testid="toggle-assistant-button"
              size="small"
              onClick={toggleAssistantCollapsed}
              sx={{
                color: "grey.600",
                height: 32,
                width: 32,
                "&:hover": { backgroundColor: "grey.100", color: "grey.800" },
              }}
            >
              {assistantPosition === "left" ? (
                isAssistantCollapsed ? (
                  <PanelLeftOpen size={18} />
                ) : (
                  <PanelLeftClose size={18} />
                )
              ) : isAssistantCollapsed ? (
                <PanelRightOpen size={18} />
              ) : (
                <PanelRightClose size={18} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    );
  };

  // Loading state
  const loadingContent = (
    <Stack
      height="100%"
      minHeight={`calc(100vh - ${HEADER_HEIGHT} - ${ROUTE_TABS_HEIGHT}px)`}
      flex={1}
      alignItems="center"
      justifyContent="center"
      bgcolor="common.pageBackground"
    >
      <CircularProgress sx={{ color: "grey.400" }} />
    </Stack>
  );

  return (
    <>
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <RouteTabs
            tabs={tabs}
            currentTab={getValidTabValue()}
            actions={renderActions()}
            showTabs={!isReportEmbed}
          >
            {isLoading ? loadingContent : routes}
          </RouteTabs>
        </Box>

        {showAnalysis && datasetId && (
          <DatasetAssistantPanel
            datasetId={datasetId}
            isCollapsed={isAssistantCollapsed}
            onPositionChange={setAssistantPosition}
          />
        )}
      </Box>

      {isShareModalOpen && dataset && (
        <CollaboratorsModal
          open={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          dataset={dataset}
          datasetId={datasetId}
        />
      )}
    </>
  );
};

export default DatasetDetails;
